import { CANNOT_RENDER_HERE, type ExportRow, type RowRun } from "./languageExports.js";
import type { Preset } from "./presets.js";

/**
 * The Export page's layout in words and arithmetic: which tab of the side
 * panel is open, what a video row's status chip says, which sizes are cards
 * and which are only mentioned, and the line under the page title. Kept apart
 * from the components so the tests at the root can run it without React.
 */

/** Views is a page of its own now, beside Edit and Export & share in the project nav. */
export const EXPORT_TABS = ["download", "share"] as const;
export type ExportTab = (typeof EXPORT_TABS)[number];

/** The tab `?tab=` asks for; anything else, or nothing, is Download. */
export function tabFromQuery(value: string | null): ExportTab {
  return (EXPORT_TABS as readonly string[]).includes(value ?? "") ? (value as ExportTab) : "download";
}

/** The tab the arrow keys land on from `current`, wrapping at both ends. */
export function tabAfter(current: ExportTab, key: string): ExportTab | null {
  const at = EXPORT_TABS.indexOf(current);
  if (key === "ArrowRight") return EXPORT_TABS[(at + 1) % EXPORT_TABS.length]!;
  if (key === "ArrowLeft") return EXPORT_TABS[(at - 1 + EXPORT_TABS.length) % EXPORT_TABS.length]!;
  if (key === "Home") return EXPORT_TABS[0];
  if (key === "End") return EXPORT_TABS[EXPORT_TABS.length - 1]!;
  return null;
}

export type ChipTone = "ok" | "muted" | "warn" | "bad" | "busy";

/**
 * What a video row's chip says. Short, because it sits in a narrow column;
 * a longer reason (why a render failed, why this browser can't) is `detail`,
 * said on its own line under the row.
 */
export interface RowChip {
  label: string;
  tone: ChipTone;
  /** 0–1 while it renders or saves. */
  progress?: number;
  detail?: string;
}

export function rowChip(
  row: Pick<ExportRow, "code" | "state" | "outOfDate">,
  run: RowRun | undefined,
  canRender: boolean | null,
): RowChip {
  if (run?.phase === "rendering" || run?.phase === "saving") {
    const percent = Math.round(run.progress * 100);
    const word = run.phase === "saving" ? "Saving" : run.where === "servers" ? "On our servers" : "Rendering";
    return { label: `${word} · ${percent}%`, tone: "busy", progress: run.progress };
  }
  if (run?.phase === "waiting") return { label: "Waiting", tone: "muted" };
  if (run?.phase === "failed") return { label: "Didn't finish", tone: "bad", detail: run.message };
  if (row.code !== null && canRender === false) {
    return { label: "Can't render here", tone: "muted", detail: CANNOT_RENDER_HERE };
  }
  if (row.outOfDate) return { label: "Out of date", tone: "warn" };
  if (row.state === "exported") return { label: "Exported", tone: "ok" };
  return { label: "Not exported", tone: "muted" };
}

/**
 * The sizes as cards, and the ones not offered yet as one line: "4K is
 * coming soon." rather than two dashed cards nobody can press.
 */
export function qualityChoices(presets: readonly { option: Preset; index: number }[]): {
  cards: { option: Preset; index: number }[];
  comingSoon: string | null;
} {
  const cards = presets.filter(({ option }) => !option.comingSoon);
  const soon = [
    ...new Set(presets.filter(({ option }) => option.comingSoon).map(({ option }) => option.label.split(" · ")[0]!)),
  ];
  return {
    cards,
    comingSoon: soon.length > 0 ? `${soon.join(" and ")} ${soon.length > 1 ? "are" : "is"} coming soon.` : null,
  };
}

const clock = (date: Date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** When an export was made, the way the page title's line says it: "today 17:02", "yesterday 09:24", "3 Oct 17:02". */
export function exportedWhen(at: Date, now: Date): string {
  if (sameDay(at, now)) return `today ${clock(at)}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(at, yesterday)) return `yesterday ${clock(at)}`;
  const day = at.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${day} ${clock(at)}`;
}

/** The line under "Export and share": duration · size · frame rate, and when it was last exported. */
export function exportMeta(parts: {
  duration: string | null;
  width: number;
  height: number;
  fps: number;
  exportedAt: Date | null;
  now: Date;
}): string {
  return [
    parts.duration,
    `${parts.width}×${parts.height}`,
    `${parts.fps} fps`,
    parts.exportedAt ? `Exported ${exportedWhen(parts.exportedAt, parts.now)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** A video's length as people say it: "5:12", "1:02:07". */
export function videoLength(seconds: number): string {
  const total = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}
